import type { JSX } from "react";
import type { RouteObject } from "react-router";
import { CarriersPage } from "./pages/admin/Carriers";
import { IntegrationsPage } from "./pages/admin/IntegrationsCatalog";
import { AdminHomePage } from "./pages/admin/AdminHome";
import { AdminFrame } from "./pages/admin/AdminFrame";
import { AuditPage, HealthPage, TeamPage } from "./pages/admin/AdminCenter";
import { CompanyPage } from "./pages/admin/Company";
import { ProblemsPage } from "./pages/admin/Problems";
import { UiGallery } from "./pages/admin/UiGallery";
import { DashboardPage } from "./pages/Dashboard";
import { CustomerDetail } from "./pages/customers/CustomerDetail";
import { CustomersList } from "./pages/customers/CustomersList";
import { LeadsPage } from "./pages/customers/Leads";
import { ShipmentPage, ShipmentsPage } from "./pages/freight/Shipments";
import { CarrierHealthPage } from "./pages/freight/CarrierHealth";
import { DispatchPage } from "./pages/freight/Dispatch";
import { ClaimPage, ClaimsPage } from "./pages/freight/Claims";
import { CreditPage } from "./pages/freight/Credit";
import { BillingPage, BillingShipmentPage } from "./pages/freight/Billing";
import { AmFastPage, AmFastShipmentPage } from "./pages/freight/AmFast";
import { ImportPage } from "./pages/freight/Import";
import { TicketPage, TicketsPage } from "./pages/service/Tickets";
import { ArticleEditor, KnowledgePage } from "./pages/service/Knowledge";
import { InboxPage } from "./pages/email/Inbox";
import { EmailTemplatesPage } from "./pages/email/Templates";
import { AudiencesPage, CampaignEditor, CampaignsPage } from "./pages/marketing/Marketing";
import { ReportPage, ReportsPage } from "./pages/insights/Reports";
import { FlexAiPage } from "./pages/insights/FlexAi";
import { SignupsPage } from "./pages/customers/Signups";
import { NotFound, Placeholder } from "./pages/Placeholder";
import { KioskWallboard, wallKey, WallboardPage } from "./pages/Wallboard";
import { QuotesList } from "./pages/quotes/QuotesList";
import { QuotingCenter } from "./pages/quotes/QuotingCenter";
import { ConsoleGate } from "./auth/ConsoleGate";
import { SetPasswordPage } from "./auth/SetPassword";
import { ConsoleShell } from "./shell/ConsoleShell";
import { allNavItems } from "./shell/nav";

/** Screens that are built; every other nav item shows the honest placeholder. */
const BUILT: Record<string, JSX.Element> = {
  "/": <DashboardPage />,
  "/admin": <AdminHomePage />,
  "/admin/carriers": <AdminFrame><CarriersPage /></AdminFrame>,
  "/admin/integrations": <IntegrationsPage />,
  "/admin/team": <AdminFrame><TeamPage /></AdminFrame>,
  "/admin/company": <AdminFrame><CompanyPage /></AdminFrame>,
  "/admin/problems": <AdminFrame><ProblemsPage /></AdminFrame>,
  "/admin/audit": <AdminFrame><AuditPage /></AdminFrame>,
  "/admin/health": <AdminFrame><HealthPage /></AdminFrame>,
  "/quotes/new": <QuotingCenter />,
  "/quotes": <QuotesList />,
  "/customers": <CustomersList />,
  "/signups": <SignupsPage />,
  "/leads": <LeadsPage />,
  "/shipments": <ShipmentsPage />,
  "/carrier-health": <CarrierHealthPage />,
  "/dispatch": <DispatchPage />,
  "/claims": <ClaimsPage />,
  "/credit": <CreditPage />,
  "/billing": <BillingPage />,
  "/amfast": <AmFastPage />,
  "/import": <ImportPage />,
  "/tickets": <TicketsPage />,
  "/knowledge": <KnowledgePage />,
  "/inbox": <InboxPage />,
  "/inbox/templates": <EmailTemplatesPage />,
  "/marketing/campaigns": <CampaignsPage />,
  "/marketing/audiences": <AudiencesPage />,
  "/reports": <ReportsPage />,
  "/ai": <FlexAiPage />,
};

export const routes: RouteObject[] = [
  // Staff invite and password-reset links land here (outside the gate: the person has no session yet).
  { path: "/set-password", element: <SetPasswordPage /> },
  { path: "/reset-password", element: <SetPasswordPage /> },
  // A.43: full-screen TV view, outside the app shell but behind the same staff sign-in gate.
  {
    path: "/wall",
    element: <WallRoute />,
  },
  {
    path: "/",
    element: (
      <ConsoleGate>
        <ConsoleShell />
      </ConsoleGate>
    ),
    children: [
      ...allNavItems.map((item) => {
        const element = BUILT[item.to] ?? <Placeholder />;
        return item.to === "/" ? { index: true, element } : { path: item.to.slice(1), element };
      }),
      // Design-system gallery (visual QA): staff-only behind the console gate, not in the sidebar.
      { path: "admin/ui", element: <UiGallery /> },
      { path: "customers/:orgId", element: <CustomerDetail /> },
      { path: "shipments/:shipmentId", element: <ShipmentPage /> },
      { path: "tickets/:ticketId", element: <TicketPage /> },
      { path: "claims/:orgId/:claimId", element: <ClaimPage /> },
      { path: "billing/:orgId/:shipmentId", element: <BillingShipmentPage /> },
      { path: "amfast/:orgId/:shipmentId", element: <AmFastShipmentPage /> },
      { path: "knowledge/:articleId", element: <ArticleEditor /> },
      { path: "marketing/campaigns/:campaignId", element: <CampaignEditor /> },
      { path: "reports/:reportId", element: <ReportPage /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

/** /wall: a TV opened with a wallboard link runs on that link's key alone; otherwise it needs a staff sign-in. */
function WallRoute() {
  const key = wallKey();
  return key ? (
    <KioskWallboard wallKeyValue={key} />
  ) : (
    <ConsoleGate>
      <WallboardPage />
    </ConsoleGate>
  );
}
